import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ShoppingBag } from "lucide-react";
import { Reveal, SectionHeading } from "../../components/primitives";
import { STORE_CATEGORIES, ProductCard, WhyShopDwc, StoreToTrainingBand } from "../../store/components";

const API = process.env.REACT_APP_BACKEND_URL;

const Row = ({ title, products, link, testId }) =>
  products.length > 0 && (
    <section className="bg-white py-14 md:py-18" data-testid={testId}>
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex items-end justify-between">
          <h2 className="text-xl font-extrabold tracking-tight text-dwc-charcoal md:text-2xl">{title}</h2>
          {link && (
            <Link to={link} className="group inline-flex items-center gap-1.5 text-[12px] font-extrabold uppercase tracking-wide text-dwc-green">
              View All <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          )}
        </div>
        <div className="mt-6 grid grid-cols-2 gap-4 md:gap-5 lg:grid-cols-4">
          {products.slice(0, 4).map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );

const StoreHome = () => {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    axios.get(`${API}/api/store/products`).then((r) => setProducts(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);
  const byCat = (c) => products.filter((p) => p.category === c);
  const recommended = products.filter((p) => p.recommended);

  return (
    <>
      <section className="relative overflow-hidden bg-dwc-green pb-20 pt-36 md:pb-28 md:pt-44 grain" data-testid="store-hero">
        <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-white/10" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-[11px] font-extrabold uppercase tracking-[0.25em] text-emerald-100">
              <ShoppingBag className="h-3.5 w-3.5" aria-hidden="true" /> DWC Store
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 max-w-3xl text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Drive Better.<br />
              <span className="text-emerald-200">Drive Smarter.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/80 md:text-lg">
              Driving and car essentials selected to make your everyday driving experience more comfortable, convenient and organised.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-9 flex flex-col gap-4 sm:flex-row">
              <Link to="/store/shop" data-testid="store-shop-now-btn" className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-7 py-4 text-sm font-bold uppercase tracking-wide text-dwc-green shadow-lg transition-transform duration-300 hover:-translate-y-0.5">
                Shop Now <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <a href="#store-categories" data-testid="store-explore-btn" className="inline-flex items-center justify-center rounded-xl border-2 border-white/40 px-7 py-4 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-white/10">
                Explore Categories
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="store-categories" className="bg-dwc-soft py-20 md:py-24" data-testid="shop-by-need-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading eyebrow="Shop By Need" number="01" title="What Does Your Driving Need?" />
          <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            {STORE_CATEGORIES.map((c, i) => (
              <Reveal key={c.slug} delay={(i % 6) * 0.06} className="h-full">
                <Link
                  to={`/store/shop?category=${c.slug}`}
                  data-testid={`category-card-${c.slug}`}
                  className="group flex h-full flex-col items-center rounded-2xl border border-dwc-green/10 bg-white p-6 text-center transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgb(23,107,58,0.10)]"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-dwc-soft text-dwc-green transition-colors duration-300 group-hover:bg-dwc-green group-hover:text-white">
                    <c.icon className="h-6 w-6" aria-hidden="true" />
                  </span>
                  <h3 className="mt-4 text-sm font-extrabold uppercase tracking-wide text-dwc-charcoal">{c.name}</h3>
                  <p className="mt-1.5 text-[12px] leading-relaxed text-dwc-charcoal/55">{c.blurb}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Row title="DWC Recommended" products={recommended} link="/store/shop?recommended=1" testId="recommended-row" />
      <Row title="Night Driving Essentials" products={byCat("night-driving")} link="/store/shop?category=night-driving" testId="night-row" />
      <Row title="Driving Essentials" products={byCat("driving-essentials")} link="/store/shop?category=driving-essentials" testId="essentials-row" />
      <Row title="Car Comfort" products={byCat("car-comfort")} link="/store/shop?category=car-comfort" testId="comfort-row" />
      <Row title="Car Care" products={byCat("car-care")} link="/store/shop?category=car-care" testId="care-row" />
      <Row title="Car Utility" products={byCat("car-utility")} link="/store/shop?category=car-utility" testId="utility-row" />
      <WhyShopDwc />
      <StoreToTrainingBand />
    </>
  );
};

export default StoreHome;
